import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { Building2, CornerDownLeft, Search } from 'lucide-react';
import { cn } from '@/lib/cn';
import { DialogOverlay, DialogPortal } from '@/components/ui/Dialog';
import { defineAbilitiesFor } from '@/lib/abilities';
import { useAuthStore } from '@/store/authStore';
import { platformSidebarGroups, type PlatformSidebarItem } from '@/components/layout/sidebarNav';
import { listMerchants, type MerchantCard } from '@/api/merchants.api';

/**
 * Global command palette (Ctrl/Cmd-K) for the platform admin.
 *
 * Two result sources:
 *  1. Navigation — flattened sidebar nav config, filtered by the query,
 *     gated by the same CASL abilities the sidebar uses.
 *  2. Merchants — debounced (300ms) query against the existing merchants
 *     list endpoint (reuses `search` param), top 8 results linking to
 *     /merchants/:id.
 *
 * Keyboard: ArrowUp/ArrowDown to move, Enter to activate, Escape to close.
 *
 * Follow-up: tickets / users search are not wired here yet — they can be
 * added as additional result sources once their list endpoints expose a
 * search param.
 */

interface NavResult {
    kind: 'nav';
    id: string;
    label: string;
    to: string;
}

interface MerchantResult {
    kind: 'merchant';
    id: string;
    label: string;
    sublabel: string | null;
    to: string;
}

type PaletteResult = NavResult | MerchantResult;

interface FlatNavEntry {
    to: string;
    i18nKey: string;
    action: PlatformSidebarItem['action'];
    subject: PlatformSidebarItem['subject'];
}

function flattenNav(): FlatNavEntry[] {
    const entries: FlatNavEntry[] = [];
    const seen = new Set<string>();
    for (const group of platformSidebarGroups) {
        for (const item of group.items) {
            if (!seen.has(item.to)) {
                seen.add(item.to);
                entries.push({ to: item.to, i18nKey: item.i18nKey, action: item.action, subject: item.subject });
            }
            for (const child of item.children ?? []) {
                if (!seen.has(child.to)) {
                    seen.add(child.to);
                    entries.push({ to: child.to, i18nKey: child.i18nKey, action: child.action, subject: child.subject });
                }
            }
        }
    }
    return entries;
}

export function CommandPalette({
    open,
    onOpenChange,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}): JSX.Element {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const role = useAuthStore((s) => s.user?.role);
    const ability = useMemo(() => defineAbilitiesFor(role), [role]);

    const [query, setQuery] = useState('');
    const [debounced, setDebounced] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const listRef = useRef<HTMLUListElement>(null);

    // Reset transient state whenever the palette opens.
    useEffect(() => {
        if (open) {
            setQuery('');
            setDebounced('');
            setActiveIndex(0);
        }
    }, [open]);

    // Debounce the query used for the merchant search (300ms).
    useEffect(() => {
        const handle = window.setTimeout(() => setDebounced(query.trim()), 300);
        return () => window.clearTimeout(handle);
    }, [query]);

    const navEntries = useMemo(() => flattenNav(), []);

    const navResults = useMemo<NavResult[]>(() => {
        const q = query.trim().toLowerCase();
        return navEntries
            .filter((entry) => ability.can(entry.action, entry.subject))
            .map((entry) => ({ kind: 'nav' as const, id: `nav:${entry.to}`, label: t(entry.i18nKey, entry.i18nKey), to: entry.to }))
            .filter((result) => (q ? result.label.toLowerCase().includes(q) : true))
            .slice(0, 8);
    }, [navEntries, ability, query, t]);

    const canReadMerchants = ability.can('read', 'Merchant');
    const merchantQuery = useQuery({
        queryKey: ['command-palette-merchants', debounced],
        queryFn: () => listMerchants({ search: debounced, limit: 8, page: 1 }),
        enabled: open && canReadMerchants && debounced.length >= 2,
        staleTime: 30_000,
    });

    const merchantResults = useMemo<MerchantResult[]>(() => {
        const merchants: MerchantCard[] = merchantQuery.data?.merchants ?? [];
        return merchants.slice(0, 8).map((m) => ({
            kind: 'merchant' as const,
            id: `merchant:${m.id}`,
            label: m.name,
            sublabel: m.email,
            to: `/merchants/${m.id}`,
        }));
    }, [merchantQuery.data]);

    const results = useMemo<PaletteResult[]>(
        () => [...navResults, ...merchantResults],
        [navResults, merchantResults],
    );

    // Keep the active index in range as results change.
    useEffect(() => {
        setActiveIndex((current) => (results.length === 0 ? 0 : Math.min(current, results.length - 1)));
    }, [results.length]);

    const activate = (result: PaletteResult | undefined): void => {
        if (!result) return;
        onOpenChange(false);
        navigate(result.to);
    };

    const onKeyDown = (event: React.KeyboardEvent): void => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((i) => (results.length === 0 ? 0 : (i + 1) % results.length));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((i) => (results.length === 0 ? 0 : (i - 1 + results.length) % results.length));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            activate(results[activeIndex]);
        }
        // Escape is handled by Radix Dialog.
    };

    // Ensure the active row stays scrolled into view.
    useEffect(() => {
        const container = listRef.current;
        if (!container) return;
        const activeEl = container.querySelector<HTMLElement>('[data-active="true"]');
        activeEl?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);

    const showMerchantHint = canReadMerchants && debounced.length >= 2 && merchantQuery.isFetching;

    return (
        <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
            <DialogPortal>
                <DialogOverlay />
                <DialogPrimitive.Content
                    onKeyDown={onKeyDown}
                    aria-label={t('nav.searchConsole', 'Search console')}
                    className={cn(
                        'fixed left-1/2 top-[max(0.75rem,8dvh)] z-[70] flex max-h-[min(70dvh,calc(100dvh-1.5rem))] w-[calc(100vw-2rem)] max-w-[560px] -translate-x-1/2 flex-col overflow-hidden rounded-2xl border border-tg-border bg-white shadow-[var(--tg-shadow-pop)] focus:outline-none',
                    )}
                >
                    <DialogPrimitive.Title className="sr-only">
                        {t('nav.searchConsole', 'Search console')}
                    </DialogPrimitive.Title>
                    <div className="flex items-center gap-2 border-b border-tg-border px-4">
                        <Search className="h-4 w-4 shrink-0 text-tg-subtle" />
                        <input
                            autoFocus
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder={t('nav.searchPlaceholder', {
                                defaultValue: 'Search pages and merchants…',
                            })}
                            className="h-12 min-w-0 flex-1 bg-transparent text-sm font-medium text-tg-ink outline-none placeholder:text-tg-subtle max-sm:text-base"
                        />
                    </div>

                    <ul ref={listRef} className="min-h-0 flex-1 overflow-y-auto p-2" role="listbox">
                        {navResults.length > 0 && (
                            <li className="px-2 pb-1 pt-2 text-[0.66rem] font-bold uppercase tracking-[0.14em] text-tg-subtle">
                                {t('palette.navigation', { defaultValue: 'Navigation' })}
                            </li>
                        )}
                        {navResults.map((result) => {
                            const index = results.indexOf(result);
                            const active = index === activeIndex;
                            return (
                                <li key={result.id}>
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={active}
                                        data-active={active}
                                        onMouseEnter={() => setActiveIndex(index)}
                                        onClick={() => activate(result)}
                                        className={cn(
                                            'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-start text-sm font-medium text-tg-ink transition-colors',
                                            active ? 'bg-tg-teal/10 text-tg-ink' : 'hover:bg-tg-surface-2',
                                        )}
                                    >
                                        <span className="min-w-0 flex-1 truncate">{result.label}</span>
                                        {active && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-tg-subtle" />}
                                    </button>
                                </li>
                            );
                        })}

                        {merchantResults.length > 0 && (
                            <li className="px-2 pb-1 pt-3 text-[0.66rem] font-bold uppercase tracking-[0.14em] text-tg-subtle">
                                {t('palette.merchants', { defaultValue: 'Merchants' })}
                            </li>
                        )}
                        {merchantResults.map((result) => {
                            const index = results.indexOf(result);
                            const active = index === activeIndex;
                            return (
                                <li key={result.id}>
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={active}
                                        data-active={active}
                                        onMouseEnter={() => setActiveIndex(index)}
                                        onClick={() => activate(result)}
                                        className={cn(
                                            'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-start text-sm text-tg-ink transition-colors',
                                            active ? 'bg-tg-teal/10' : 'hover:bg-tg-surface-2',
                                        )}
                                    >
                                        <Building2 className="h-4 w-4 shrink-0 text-tg-subtle" />
                                        <span className="flex min-w-0 flex-1 flex-col">
                                            <span className="truncate font-medium">{result.label}</span>
                                            {result.sublabel && (
                                                <span className="truncate text-xs text-tg-subtle">{result.sublabel}</span>
                                            )}
                                        </span>
                                        {active && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-tg-subtle" />}
                                    </button>
                                </li>
                            );
                        })}

                        {results.length === 0 && (
                            <li className="px-3 py-8 text-center text-sm text-tg-subtle">
                                {showMerchantHint
                                    ? t('palette.searching', { defaultValue: 'Searching…' })
                                    : t('palette.empty', { defaultValue: 'No results found.' })}
                            </li>
                        )}
                    </ul>
                </DialogPrimitive.Content>
            </DialogPortal>
        </DialogPrimitive.Root>
    );
}
